import React from "react";
import { observer } from "mobx-react-lite";
import { Tag } from "antd";
import classNames from "classnames";
import Card from "@app/components/card/card";

import Title from "../chunks/title";
import Authors from "../chunks/authors";
import MissingAbstract from "../chunks/missing-abstract";
import CommentsTag from "@app/components/comments/chunks/comments-tag";
import HBox from "@app/components/container/hbox/hbox";
import DropArea from "@app/components/form/upload/drop/area";
import { MultiSelectCheckBox } from "@app/components/multi-select/multi-select";
import { useContext } from "@app/components/multi-select/context";

import "./style/card.scoped.scss";

import { SearchType } from "@app/constants";
import { CloudSyncOutlined } from "@ant-design/icons";
import ArticleTags from "../chunks/article-tags";
import Source from "../chunks/source";
import reportStore from "@app/state/store/report";

import Menu from "./menu";
import state from "@app/state/store/report/article";

const L2Card = observer(
    ({
        article,
        type = SearchType.SLR,
        refStyle,
        keywords = [],
        checkedBox,
        className,
        readonly = false,
        ...rest
    }) => {
        const review = article.review.l2 || {};
        const config = reportStore.config?.[type].l2;
        const useScore = config?.enableScore;
        /**
         * Render the missing file tag
         */
        const { context } = useContext();
        const { isChecked, toggle } = context;

        const MissingFile = observer(() => {
            const { file, isDownloading } = article;

            if (file) {
                return;
            } else {
                return isDownloading ? (
                    <CloudSyncOutlined className="icon" />
                ) : (
                    <Tag color="red">Missing File</Tag>
                );
            }
        });

        const Score = observer(({ article }) => {
            if (!review.score) {
                return;
            }

            return (
                <Tag className="score">
                    Score: <em>{review.score}</em>
                </Tag>
            );
        });

        const onUpload = async (file) => {
            state.updateArticleById(article._id, { file: file._id });
        };

        /**
         * Render the article's L2 status
         */
        const Status = () => {
            const l2Complete =
                type === SearchType.SLR ? article.slrL2Complete : article.sotaL2Complete;
            const l2Passed = type === SearchType.SLR ? article.slrL2Passed : article.sotaL2Passed;

            if (!review.started) {
                return <Tag>Pending</Tag>;
            }

            if (review.started && !l2Complete) {
                return <Tag color="blue">In Progress</Tag>;
            }

            if (l2Passed) {
                return <Tag color="green">Included</Tag>;
            }

            return <Tag color="red">Excluded</Tag>;
        };

        const reason = article.review?.l2?.fail?.reason;

        return (
            <Card
                className={classNames("result l2", className)}
                scrollIntoView
                {...rest}
                selected={isChecked ? isChecked(article._id) : false}
                onClick={(e) => {
                    if (e.ctrlKey || e.metaKey) {
                        e.preventDefault();
                        toggle(article._id);
                    }

                    if (e.shiftKey) {
                        e.preventDefault();
                        toggle(article._id, { range: true });
                    }
                }}
            >
                <DropArea
                    onChange={onUpload}
                    accept=".pdf,application/pdf"
                    disabled={readonly}
                    className="small"
                >
                    <Menu
                        article={article}
                        onUpload={onUpload}
                        className="menu"
                        isL2={true}
                        readonly={readonly}
                    />
                    {checkedBox && <MultiSelectCheckBox id={article._id} />}
                    <div className={`${checkedBox && "shiftRight"}`}>
                        <Title article={article} keywords={keywords} />
                        <Authors article={article} />
                    </div>
                    <Source article={article} refStyle={refStyle} trim />
                    {reason && <div className="reason">{reason}</div>}

                    <HBox className="mt-20">
                        <HBox.Left className="vtop">
                            {/* <SourceTags article={article} /> */}
                            <ArticleTags article={article} />
                            {article.totalComments > 0 && (
                                <CommentsTag count={article.totalComments} />
                            )}
                        </HBox.Left>
                        <HBox.Right shrink={true} className="vtop">
                            <MissingAbstract article={article} />
                            <MissingFile />
                            <Status />
                            {useScore && <Score article={article} />}
                        </HBox.Right>
                    </HBox>
                </DropArea>
            </Card>
        );
    },
);

export default L2Card;
